import React from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ExamBuilder } from '../studio/ExamBuilder';
import { getSubjectById } from '../studio/constants/subjectCatalog';
import { buildCourseDashboardPath } from '../studio/constants/courseStudioRoutes';
import { getCourseProfileById } from '../studio/services/courseProfileStorage';
import { hasMaterialIntentEntry } from '../studio/constants/materialIntent';
import { DELIVERY_PROFILE_LABELS } from '../studio/constants/deliveryProfile';
import { resolveStudioMaterialEntry } from '../studio/constants/studioMaterialRoutes';
import { getEvaluationExam } from '../studio/examStorage';
import { buildPrintWorksheetUrl } from '../studio/print/buildPrintWorksheetUrl';

/**
 * Entrada legacy `/studio/material|exam|guide|clase`.
 * Guías / evaluaciones imprimibles → estudio print+chat.
 * Solo clase interactiva permanece en ExamBuilder.
 */
export function StudioMaterialPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const examId = searchParams.get('exam') ?? undefined;
  const subjectId = searchParams.get('subject') ?? undefined;
  const courseId = searchParams.get('course') ?? undefined;
  const subject = subjectId ? getSubjectById(subjectId) : undefined;
  const course = courseId ? getCourseProfileById(courseId) : null;
  const initialExam = examId ? getEvaluationExam(examId) : null;

  if (!hasMaterialIntentEntry(searchParams, examId)) {
    if (courseId) {
      return <Navigate to={buildCourseDashboardPath(courseId)} replace />;
    }
    if (subjectId) {
      return <Navigate to={`/studio/asignatura/${subjectId}`} replace />;
    }
    return <Navigate to="/studio" replace />;
  }

  const entry = resolveStudioMaterialEntry(location.pathname, searchParams, initialExam);
  if (!entry) {
    if (courseId) {
      return <Navigate to={buildCourseDashboardPath(courseId)} replace />;
    }
    return <Navigate to="/studio" replace />;
  }

  const { profile, intent, resourceType } = entry;
  const isInteractiveClass = profile === 'interactive_class';

  // Editor canónico para material imprimible / guías / evaluación en papel.
  if (!isInteractiveClass && examId) {
    const state = location.state as { generatedNotice?: string; fromGeneration?: boolean } | null;
    return (
      <Navigate
        to={buildPrintWorksheetUrl(examId, { edit: true })}
        replace
        state={{
          fromGeneration: Boolean(state?.fromGeneration || state?.generatedNotice),
          ...(state?.generatedNotice ? { generatedNotice: state.generatedNotice } : {}),
        }}
      />
    );
  }

  const profileLabel = DELIVERY_PROFILE_LABELS[profile];
  const backTo = course
    ? buildCourseDashboardPath(course.id)
    : subject
      ? `/studio/asignatura/${subject.id}`
      : '/studio';

  return (
    <div className="studio-page w-full min-w-0">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Link to={backTo} className="sala-label text-[var(--sala-accent-neon)]">
          ← {course ? course.displayName : 'Studio'}
        </Link>
        {subject && (
          <>
            <span className="text-[var(--sala-ink-dim)]">/</span>
            <span className="sala-label">
              {subject.icon} {subject.name}
            </span>
          </>
        )}
        <span className="text-[var(--sala-ink-dim)]">/</span>
        <span className="sala-label sala-phase-badge--live inline-flex !m-0 !p-1.5 !px-2.5">
          {profileLabel}
        </span>
      </div>
      <ExamBuilder
        examId={examId}
        defaultSubjectTag={subject?.tag ?? course?.subjectTag}
        defaultResourceType={resourceType}
        materialIntent={intent}
        courseId={courseId}
      />
    </div>
  );
}
